/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactNode, useMemo} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import isEqual from 'lodash.isequal';
import {DownloadJob, GenericObject} from '@ion/core/src/types/entities';
import {getClasses} from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';

export interface DownloadJobViewDialogProps {
  styleOverrides?: (theme: DLSTheme) => GenericObject,
  onCloseDialog?: (e: MouseEvent) => void;
  children: ReactNode;
  open: boolean;
  downloadjobs: DownloadJob;
  index?: number;
}

const styles = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    minWidth                       : '73%',
    overflow                       : 'hidden',
    margin                         : 'auto',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)',
    minHeight : '480px'
  },
  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },
  titleWrapper : {
    display : 'flex'
  },
  formTitle : {
    display        : 'flex',
    justifyContent : 'left',
    alignItems     : 'flex-start',
    width          : '100%'
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  dialogTitle : {
    padding      : '4px 30px',
    whiteSspace  : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  },
  formDialogContent : {
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  formActions : {
    borderTop                      : `solid 1px ${dlsColor('grey4')}`,
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'space-between',
      padding        : '8px 16px'
    }
  }
});

/**
 * It will display Job Detail and grid in the editor
 *
 * ```typescript
 *
 * import {DownloadJobViewDialog} from '@ion/dls/src/editors/';
 *
 * <DownloadJobViewDialog open={open}>
 *   <Component />
 * </DownloadJobViewDialog>
 *
 * ```
 *
 */
export const DownloadJobViewDialog = ({
  children,
  onCloseDialog,
  styleOverrides,
  open,
  downloadjobs,
  index
}: DownloadJobViewDialogProps) => {
  const dialogStyles = useExtendStyles(styles, styleOverrides);
  const classes      = dialogStyles();
  const titles       = useMemo(function() {
    return downloadjobs?.tasks?.map(({task}) => task?.metaData?.title);
  }, [downloadjobs], isEqual);

  return (
    <Dialog open={open}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.titleWrapper}>
          <div className={classes.formTitle}>
            <div onClick={onCloseDialog} className={classes.closeButton}>
              <MaterialExitCloseIcon size={IconSize.medium}
                color={dlsColor('grey3')} />
            </div>
            <HeaderTypography variant='h4' className={classes.dialogTitle}>
              {titles[index] ? titles[index] + ' - All Devices' : 'All Devices'}
            </HeaderTypography>
          </div>
        </div>
      </DialogTitle>
      <DialogContent className={classes.formDialogContent}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default DownloadJobViewDialog;
